import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { setDataToast, setVisibleToast } from 'features/toast';
import { setLoading } from 'features/loader';
import { postRegister, getDiplomadosPublicos } from './registerClient';

interface RegisterForm {
  numeroTelefono: string;
  primerNombre: string;
  segundoNombre: string;
  tercerNombre: string;
  primerApellido: string;
  segundoApellido: string;
  cui: string;
  idGenero: number | '';
  fechaNacimiento: string | null;
}

interface RegisterState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  form: RegisterForm;
  completedCarnet: string | null;
  availableDiplomados: any[];
  hasAvailableDiplomados: boolean;
  checkingDiplomados: boolean;
}

interface CreateAlumnoPayload {
  redirectToLogin?: boolean;
}

const initialState: RegisterState = {
  status: 'idle',
  error: null,
  completedCarnet: null,
  availableDiplomados: [],
  hasAvailableDiplomados: false,
  checkingDiplomados: false,
  form: {
    numeroTelefono: '',
    primerNombre: '',
    segundoNombre: '',
    tercerNombre: '',
    primerApellido: '',
    segundoApellido: '',
    cui: '',
    idGenero: '',
    fechaNacimiento: null,
  },
};

export const checkAvailableDiplomados = createAsyncThunk(
  'register/checkAvailableDiplomados',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await getDiplomadosPublicos();
      dispatch(setLoading(false));

      if (response.result) {
        const diplomados = response.data || [];
        const availableDiplomados = Array.isArray(diplomados) ? diplomados : [];

        return {
          diplomados: availableDiplomados,
          hasAvailable: availableDiplomados.length > 0
        };
      }

      return rejectWithValue(response.message || 'No fue posible verificar diplomados disponibles');
    } catch (error: any) {
      dispatch(setLoading(false));
      return rejectWithValue(error?.message || 'Error al verificar diplomados disponibles');
    }
  }
);

export const createAlumnoPublic = createAsyncThunk(
  'register/createAlumnoPublic',
  async ({ redirectToLogin = true }: CreateAlumnoPayload, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { form } = state.register;
    dispatch(setLoading(true));

    const body = {
      numeroTelefono: form.numeroTelefono,
      primerNombre: form.primerNombre,
      segundoNombre: form.segundoNombre || null,
      tercerNombre: form.tercerNombre || null,
      primerApellido: form.primerApellido,
      segundoApellido: form.segundoApellido || null,
      cui: form.cui,
      idGenero: form.idGenero || null,
      fechaNacimiento: form.fechaNacimiento,
    };

    try {
      const { result, message, data } = await postRegister('', body);
      dispatch(setLoading(false));

      if (result) {
        const carnet = typeof data === 'string' ? data : null;

        dispatch(
          setDataToast({
            title: 'Registro exitoso',
            message: 'Tu cuenta ha sido creada exitosamente. Recibirás tus credenciales de acceso por SMS. Revisa tu teléfono e ingresa al portal para continuar.',
            data: null,
            type: 'LimeGreen',
            time: Date.now(),
          })
        );
        dispatch(setVisibleToast(true));
        return { message, carnet, redirectToLogin };
      }

      const errorMessage = message || 'No fue posible completar el registro';
      const messageUpper = String(errorMessage).toUpperCase();
      const isCUIExists = messageUpper.includes('CUI YA REGISTRADO');
      const title = isCUIExists ? 'CUI ya registrado' : 'Acción incompleta';
      const displayMessage = isCUIExists
        ? 'El CUI ingresado ya está registrado en el sistema. Si ya tienes una cuenta, inicia sesión.'
        : errorMessage;

      dispatch(
        setDataToast({
          title,
          message: displayMessage,
          data,
          type: 'Crimson',
          time: Date.now(),
        })
      );
      dispatch(setVisibleToast(true));
      return rejectWithValue(displayMessage);
    } catch (error: any) {
      dispatch(setLoading(false));
      const message = error?.message || 'Registro fallido';
      dispatch(
        setDataToast({
          title: 'Acción incompleta',
          message,
          data: error?.data || null,
          type: 'Crimson',
          time: Date.now(),
        })
      );
      dispatch(setVisibleToast(true));
      return rejectWithValue(message);
    }
  }
);

const registerSlice = createSlice({
  name: 'register',
  initialState,
  reducers: {
    setRegisterField: (state, action: PayloadAction<{ field: keyof RegisterForm; value: any }>) => {
      state.form[action.payload.field] = action.payload.value;
    },
    resetRegisterForm: (state) => {
      state.form = initialState.form;
      state.status = 'idle';
      state.error = null;
      state.completedCarnet = null;
    },
    resetRegisterData: (state) => {
      state.completedCarnet = null;
      state.status = 'idle';
      state.error = null;
      state.availableDiplomados = [];
      state.hasAvailableDiplomados = false;
      state.checkingDiplomados = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAvailableDiplomados.pending, (state) => {
        state.checkingDiplomados = true;
        state.error = null;
      })
      .addCase(checkAvailableDiplomados.fulfilled, (state, action) => {
        state.checkingDiplomados = false;
        state.availableDiplomados = action.payload.diplomados;
        state.hasAvailableDiplomados = action.payload.hasAvailable;
      })
      .addCase(checkAvailableDiplomados.rejected, (state, action) => {
        state.checkingDiplomados = false;
        state.error = (action.payload as string) || 'Error al verificar diplomados';
        state.availableDiplomados = [];
        state.hasAvailableDiplomados = false;
      })
      .addCase(createAlumnoPublic.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createAlumnoPublic.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.completedCarnet = action.payload.carnet ?? null;
      })
      .addCase(createAlumnoPublic.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Registro fallido';
      });
  },
});

export const { setRegisterField, resetRegisterForm, resetRegisterData } = registerSlice.actions;
export const selectRegisterForm = (state: RootState) => state.register.form;
export const selectRegisterStatus = (state: RootState) => state.register.status;
export const selectRegisterError = (state: RootState) => state.register.error;
export const selectRegisterCompletedCarnet = (state: RootState) => state.register.completedCarnet;
export const selectAvailableDiplomados = (state: RootState) => state.register.availableDiplomados;
export const selectHasAvailableDiplomados = (state: RootState) => state.register.hasAvailableDiplomados;
export const selectCheckingDiplomados = (state: RootState) => state.register.checkingDiplomados;

export default registerSlice.reducer;
